import { supabase } from '../supabaseClient';
import { getOrgId } from './orgStore';

export const fetchQuotes = async (searchTerm = '') => {
  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  const [quoteRes, custRes, itemRes, taxRes, setupRes, setup2Res] =
    await Promise.all([
      supabase
        .from('quotations')
        .select('*, customers:customer_id(full_name)')
        .eq('org_id', orgId)
        .order('quotation_date', { ascending: false }),
      supabase.from('customers').select('*').eq('org_id', orgId).order('full_name', { ascending: true }),
      supabase.from('item_with_stock').select('*').eq('org_id', orgId).order('item_id', { ascending: true }),
      supabase.from('tax').select('*').or(`org_id.eq.${orgId},is_global.eq.true`),
      supabase.from('setup').select('*').eq('org_id', orgId),
      supabase.from('setup2').select('*').eq('org_id', orgId),
    ]);

  if (quoteRes.error) throw quoteRes.error;

  let quotes = (quoteRes.data || []).map((q) => ({ ...q }));

  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    quotes = quotes.filter(
      (q) =>
        q.quotation_number?.toString().toLowerCase().includes(term) ||
        q.customers?.full_name?.toLowerCase().includes(term)
    );
  }

  return {
    quotes,
    customers: custRes.data || [],
    items: itemRes.data || [],
    taxes: taxRes.data || [],
    setup: setupRes.data || [],
    setup2: setup2Res.data || [],
  };
};

export const fetchQuoteLines = async (quotationId) => {
  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  const { data, error } = await supabase
    .from('quotation_line_items')
    .select('*')
    .eq('quotation_id', quotationId)
    .eq('org_id', orgId)
    .order('line_number', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const saveQuote = async ({ header, lines }, existingQuotationId = null) => {
  const { billing_address, email, ...dbHeader } = header;
  if (billing_address) dbHeader.bill_to = billing_address;
  if (email !== undefined) dbHeader.customer_email = email;

  let quotationId = existingQuotationId;

  if (existingQuotationId) {
    const orgId = getOrgId();
    if (!orgId) throw new Error('No org selected (org_id is null).');

    const { error } = await supabase
      .from('quotations')
      .update(dbHeader)
      .eq('quotation_id', existingQuotationId);
    if (error) throw error;
  } else {
    const { quotation_id: _strip, ...insertPayload } = dbHeader;
    const { data, error } = await supabase
      .from('quotations')
      .insert([{ ...insertPayload, org_id: getOrgId() }])
      .select()
      .single();
    if (error) throw error;
    quotationId = data.quotation_id;
  }

  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  await supabase
    .from('quotation_line_items')
    .delete()
    .eq('quotation_id', quotationId)
    .eq('org_id', orgId);

  const linesToInsert = lines
    .map((line, idx) => ({
      quotation_id: quotationId,
      org_id: orgId,
      line_number: idx + 1,
      item_id: line.item_id || null,
      item_description: line.item_description,
      qty: line.quantity,
      unit_price: line.unit_price,
      line_discount_rate: line.line_discount_rate || 0,
      line_total_inclusive_tax: line.total_price,
    }))
    .filter((l) => l.item_description || l.unit_price > 0);

  if (linesToInsert.length > 0) {
    const { error: insertError } = await supabase
      .from('quotation_line_items')
      .insert(linesToInsert);
    if (insertError) throw insertError;
  }

  return quotationId;
};

export const deleteQuote = async (quotationId) => {
  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  await supabase
    .from('quotation_line_items')
    .delete()
    .eq('quotation_id', quotationId)
    .eq('org_id', orgId);

  const { error } = await supabase
    .from('quotations')
    .delete()
    .eq('quotation_id', quotationId)
    .eq('org_id', orgId);
  if (error) throw error;
};
